import React from "react";
import { Heart, Crown, Zap } from "lucide-react";

/**
 * UserBadges — small inline icons (with tooltip on hover) representing
 * the user's earned community badges. Used everywhere a username is
 * displayed (winners wall, leaderboard, profile, splash carousel,
 * Community Tank wall).
 *
 * Props:
 *  - lootLegend (bool)   → render gold crown if true
 *  - goodDeeds (number)  → render heart + count if >= 1
 *  - igniter (bool)      → render glowing ⚡ if true
 *  - badges (string[])   → fallback when explicit booleans aren't passed
 *  - size (number)       → icon size in px (default 12)
 *  - className (string)  → optional extra wrapper classes
 *  - testIdPrefix        → optional unique prefix for data-testid
 *
 * Renders nothing if there's nothing to show. Pure presentational, no fetch.
 */
export default function UserBadges({
  lootLegend,
  goodDeeds,
  igniter,
  badges,
  size = 12,
  className = "",
  testIdPrefix = "user-badge",
}) {
  const list = Array.isArray(badges) ? badges : [];
  const isLegend = lootLegend ?? list.includes("Loot Legend");
  const isIgniter = igniter ?? list.includes("Igniter");
  const deeds = typeof goodDeeds === "number"
    ? goodDeeds
    : (list.includes("Good Deed") ? 1 : 0);

  if (!isLegend && !isIgniter && deeds <= 0) return null;

  return (
    <span
      className={`inline-flex items-center gap-1.5 align-middle ${className}`}
      data-testid={`${testIdPrefix}-wrap`}
    >
      {isIgniter && (
        <span
          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#0d100d] border border-[#76B82A]/60 text-[#76B82A] shadow-[0_0_10px_rgba(118,184,42,0.55)] animate-pulse-slow"
          title="Igniter — fueled the Community Tank with $100+"
          aria-label="Igniter"
          data-testid={`${testIdPrefix}-igniter`}
        >
          <Zap size={size} strokeWidth={2.5} fill="#76B82A" />
        </span>
      )}
      {isLegend && (
        <span
          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#d7a62a] text-[#0d100d] shadow-sm"
          title="Loot Legend — earned by stacking Good Deeds across community missions"
          aria-label="Loot Legend"
          data-testid={`${testIdPrefix}-legend`}
        >
          <Crown size={size} strokeWidth={2.5} />
        </span>
      )}
      {deeds > 0 && (
        <span
          className="inline-flex items-center gap-0.5 px-1.5 h-5 rounded-full bg-[#76B82A]/15 text-[#76B82A] border border-[#76B82A]/40 splash-mono uppercase tracking-widest text-[10px]"
          title={`Good Deeds: ${deeds} — community-mission proofs approved`}
          aria-label={`${deeds} good deeds`}
          data-testid={`${testIdPrefix}-deeds`}
        >
          <Heart size={size - 2} strokeWidth={2.5} />
          <span>{deeds}</span>
        </span>
      )}
    </span>
  );
}
